/**
 * Dialog Style Tokens
 * Consistent dialog sizing and typography
 */

export const DIALOGS = {
  // Small dialogs - QC checks, confirmations, simple actions
  small: 'w-[calc(100%-2rem)] sm:w-[calc(100%-2rem)] sm:max-w-md max-h-[85vh] overflow-y-auto',
  
  // Medium dialogs - Progress displays, forms
  medium: 'w-[calc(100%-2rem)] sm:w-[calc(100%-2rem)] sm:max-w-[700px] max-h-[85vh]',
  
  // Large dialogs - Editors, complex forms (flex column layout)
  large: 'w-[calc(100%-2rem)] sm:w-[calc(100%-2rem)] sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col',
  
  // Large dialogs with scroll (data displays, detail views)
  largeScroll: 'w-[calc(100%-2rem)] sm:w-[calc(100%-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto',
  
  // Large dialogs minimal (no height/overflow constraints)
  largeMinimal: 'w-[calc(100%-2rem)] sm:w-[calc(100%-2rem)] sm:max-w-2xl',
  
  // Extra large dialogs - Full previews, comparison views
  xlarge: 'w-[calc(100%-2rem)] sm:w-[calc(100%-2rem)] sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col',
  
  // Extra large dialogs with scroll
  xlargeScroll: 'w-[calc(100%-2rem)] sm:w-[calc(100%-2rem)] sm:max-w-4xl max-h-[90vh] overflow-y-auto',
  
  // Extra large dialogs with 80vh height
  xlarge80: 'w-[calc(100%-2rem)] sm:w-[calc(100%-2rem)] sm:max-w-4xl max-h-[80vh]',
  
  // 6XL dialogs - Full editors, comparison panels
  xxlarge: 'w-[calc(100%-2rem)] sm:w-[calc(100%-2rem)] sm:max-w-6xl h-[80vh] flex flex-col',
  
  // 6XL dialogs with scroll - Auth/Plans modals
  xxlargeScroll: 'w-[calc(100%-2rem)] sm:w-[calc(100%-4rem)] sm:max-w-7xl max-h-[90vh] overflow-y-auto',
} as const;

// Dialog typography tokens - consistent across all entity dialogs
export const DIALOG_TYPOGRAPHY = {
  // Title styling
  title: 'text-base font-semibold',
  titleWithIcon: 'flex items-center gap-2 text-base font-semibold',
  
  // Description styling
  description: 'text-sm text-muted-foreground',
  
  // Progress bar
  progressBar: 'h-2',
  progressLabel: 'text-xs text-muted-foreground',
  
  // Log styling
  logContainer: 'space-y-1 font-mono text-xs',
  logTimestamp: 'text-muted-foreground/60 shrink-0 text-[10px]',
  logMessage: 'text-xs',
  
  // Activity log label
  logLabel: 'text-xs font-medium text-muted-foreground',
  logScrollArea: 'rounded-md border bg-muted/30',
} as const;

// Dialog icon sizes
export const DIALOG_ICONS = {
  titleIcon: 'h-4 w-4',
  logIcon: 'h-3 w-3',
  copyButton: 'h-3.5 w-3.5',
} as const;

export type DialogToken = keyof typeof DIALOGS;
export type DialogTypographyToken = keyof typeof DIALOG_TYPOGRAPHY;
export type DialogIconToken = keyof typeof DIALOG_ICONS;
